import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getDirection } from '@/i18n/config';

interface DirectionContextType {
  direction: 'rtl' | 'ltr';
  isRTL: boolean;
}

const DirectionContext = createContext<DirectionContextType>({
  direction: 'rtl',
  isRTL: true
});

export const useDirection = () => useContext(DirectionContext);

interface DirectionProviderProps {
  children: ReactNode;
}

export const DirectionProvider: React.FC<DirectionProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const isRTL = direction === 'rtl';

  useEffect(() => {
    // Update HTML attributes when language changes
    const html = document.documentElement;
    html.setAttribute('dir', direction);
    html.setAttribute('lang', i18n.language);

    // Update body direction for consistency
    document.body.style.direction = direction;
    document.body.style.textAlign = isRTL ? 'right' : 'left';
  }, [direction, i18n.language, isRTL]);

  return (
    <DirectionContext.Provider value={{ direction, isRTL }}>
      {children}
    </DirectionContext.Provider>
  );
};

export default DirectionProvider;
